import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import {IQuotationRetail} from "libraries/service/resources/quotation-quotations/IQuotationsQuotation";
import {Yii} from "libraries/interfaces/Yii";
import {FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {INumberFieldSpecs} from "libraries/module/formbuilder/fields/number/INumberFieldSpecs";
import {ConfigurationResourceService} from "libraries/service/resources/configuration/configurationResource.service";
import {
    defaultBlockCategoryConfig,
    IBlockCategoryConfig
} from 'app_quotation/shared/configurationDefaults/blockCategoryConfiguration';
import {FieldSpecsLayout} from "libraries/module/formbuilder/fields/interfaces/field-specs";
import {BtnStyleTypes} from "libraries/ui_button/btn.component";
import {CalculationMode, wenji} from "libraries/interfaces/IWenji";
import {EMoveMode} from "libraries/enums/e-move-mode";
import {MatMenuTrigger} from "@angular/material/menu";
import {MatRadioChange} from "@angular/material/radio";
import {QuotationBlockCategoryCodeEnum} from "libraries/interfaces/quotation-block-category-code.enum";
import {
    ICalculationConfig,
    ICommissionCalculationConfig,
    IRetailCalculationConfig
} from "../../interfaces/ICalculationConfig";
import {ICalculationCategoryValue} from "../../interfaces/ICalculationCategoryValue";

export enum WordingKeys {
    granularity = 'granularity',
    global = 'global',
    perCategory = 'perCategory',
    valid = 'valid',
    reset = 'reset'
}

export interface IMarginChangeEvent {
    calculationMode: CalculationMode;
    marginRequest: { [key: string]: number };
}

export interface ICommissionChangeEvent {
    calculationMode: CalculationMode;
    commissionRequest: { [key: string]: number };
}

@Component({
    selector: 'cell-total-margin',
    templateUrl: './cell-total-margin.component.html',
    styleUrls: ["./cell-total-margin.component.scss"],
})

export class CellTotalMarginComponent implements OnInit, OnChanges {
    @Input()
    public price: IQuotationRetail;
    @Input()
    public calculationMode: CalculationMode = CalculationMode.margin;
    @Input()
    public isSticky: boolean;
    @Input()
    public usedCategories: Map<QuotationBlockCategoryCodeEnum, number>;
    @Input()
    public parity: "odd" | "even" = "odd";
    @Input()
    public isEditable: boolean;
    @Input()
    public canEmitRightPosition: boolean;

    /**
     * Indicate that Edition is opened from keyboard navigation  in parent component
     */
    @Input()
    public isKeyboardOpen: boolean;
    @Input()
    public alternateContent: string;
    @Input()
    public retailCalculationConfig: IRetailCalculationConfig | ICommissionCalculationConfig;

    @Output()
    public readonly onChangeQuotationMargin:
        EventEmitter<IMarginChangeEvent> =
        new EventEmitter<IMarginChangeEvent>();
    @Output()
    public readonly changeFocusMovement: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output()
    public readonly closeModal: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output()
    public readonly onRightPositionChange: EventEmitter<number> = new EventEmitter<number>();
    @Output()
    public readonly onFocusField: EventEmitter<null> = new EventEmitter<null>();
    @Output()
    public readonly onMoveModeChange: EventEmitter<EMoveMode> = new EventEmitter<EMoveMode>();

    public navigationInsideModal: number = 0;
    public rowMax: number;
    public rowMaxGlobal: number = 3;
    public navigationFocus: boolean = false;
    public usedCategoriesKeys: QuotationBlockCategoryCodeEnum[] = [];
    public globalConfig: INumberFieldSpecs;
    public blockCategory: Map<QuotationBlockCategoryCodeEnum, INumberFieldSpecs> = new Map();
    public isCollapsed: boolean;
    public readonly wordingKeys: typeof WordingKeys = WordingKeys;
    public marginImpact: WordingKeys = WordingKeys.global;
    public formGroup: FormGroup;
    public initialGlobalValue: number;
    public initialCategoryValues: Map<QuotationBlockCategoryCodeEnum, number> = new Map();
    public btnType: typeof BtnStyleTypes = BtnStyleTypes;
    public wording: { [key in WordingKeys]: string } = {
        [WordingKeys.granularity]: Yii.t('wui-quotation-js', 'Granularité'),
        [WordingKeys.global]: this.calculationMode === CalculationMode.commission ?
            Yii.t('wui-quotation-js', 'Commission globale') :
            wenji.quotation.retailCalculationMode === CalculationMode.margin
                ? Yii.t('wui-quotation-js', 'Marge globale')
                : Yii.t('wui-quotation-js', 'Markup global'),
        [WordingKeys.perCategory]: this.calculationMode === CalculationMode.commission ?
            Yii.t('wui-quotation-js', 'Commission par catégorie de blocs') :
            wenji.quotation.retailCalculationMode === CalculationMode.margin
            ? Yii.t('wui-quotation-js', 'Marge par catégorie de blocs')
            : Yii.t('wui-quotation-js', 'Markup par catégorie de blocs'),
        [WordingKeys.valid]: Yii.t('wui-quotation-js', 'Valider'),
        [WordingKeys.reset]: Yii.t('wui-quotation-js', 'Valeurs par défaut'),
    };


    @ViewChild('template', { read: TemplateRef, static: true })
    private readonly template: TemplateRef<{}>;
    @ViewChild(MatMenuTrigger)
    private readonly menu: MatMenuTrigger;
    @ViewChild('navigationFocuser')
    private readonly navigationFocuser: ElementRef;
    @ViewChild('marginTotalFocuser')
    private readonly marginTotalFocuser: ElementRef;

    constructor(
        private readonly viewContainerRef: ViewContainerRef,
        private readonly formBuilder: FormBuilder,
        private readonly configurationResourceService: ConfigurationResourceService
    ) {
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.calculationMode || changes.usedCategories || changes.retailCalculationConfig) {
            this.initializeModalPercents();
            this.radioValue(this.marginImpact);
        }
    }

    public ngOnInit(): void {
        this.viewContainerRef.createEmbeddedView(this.template);
        this.changeWordingForCommission();
    }

    public marginImpactChange(choice: MatRadioChange): void {
        this.radioValue(choice.value);
    }

    public stopClick(event: Event): void {
        event.stopPropagation();
    }

    public openMenu(event: boolean): void {
        if (event) {
            setTimeout(()=>{
                this.menu.openMenu();
                if(this.marginTotalFocuser){
                    this.marginTotalFocuser.nativeElement.focus();
                }
            });
        }
        //set initial values
        this.initialGlobalValue = this.globalConfig.formGroup.get('global').value;
        for (const categoryCode of this.usedCategories.keys()) {
            this.initialCategoryValues.set(categoryCode, this.formGroup.get(categoryCode.toLowerCase()).value);
        }
        this.changeFocusMovement.emit(false);
        this.onFocusField.emit(null);
        this.onMoveModeChange.emit(EMoveMode.componentCell);
    }

    public changeCellArrow(): void {
        this.isCollapsed = false;
    }

    public keydown(event: KeyboardEvent): void {
        switch (event.key) {

            case ("Tab"):
                event.stopPropagation();
                break;

            case ("Escape"):
            case ("Esc"):
                this.menu.closeMenu();
                this.onMoveModeChange.emit(EMoveMode.grid);
                if(this.marginTotalFocuser){
                    this.marginTotalFocuser.nativeElement.blur();
                }
                this.navigationInsideModal = 0;
                this.closeModal.emit(true);
                break;

            case ("Enter"):
                event.stopPropagation();

                switch (this.navigationInsideModal) {
                    case (0):
                        this.marginImpact = this.wordingKeys.global;
                        this.rowMax = this.rowMaxGlobal;
                        this.navigationInsideModal = 2;
                        break;

                    case (1):
                        this.marginImpact = this.wordingKeys.perCategory;
                        this.rowMax = Array.from(this.usedCategories.keys()).length + 3;
                        this.navigationInsideModal = 2;
                        break;

                    case (this.rowMax):
                        if (this.marginImpact === this.wordingKeys.global) {
                            this.submitGlobal();
                        } else if (this.marginImpact === this.wordingKeys.perCategory) {
                            this.submitCategories();
                        }
                        this.onMoveModeChange.emit(EMoveMode.grid);
                        break;

                    default:
                        break;
                }
                break;
            default:
                break;
        }

        if (this.navigationInsideModal < 2 || this.navigationInsideModal === this.rowMax) {
            if(this.marginTotalFocuser){
                this.marginTotalFocuser.nativeElement.focus();
                this.onMoveModeChange.emit(EMoveMode.componentCell);
            }
        }
    }

    public submitGlobal(): void {
        this.menu.closeMenu();
        if(this.marginTotalFocuser){
            this.marginTotalFocuser.nativeElement.blur();
            this.navigationInsideModal = 0;
        }

        const marginRequest: { [key: string]: number } = {};
        Array.from(this.initialCategoryValues.keys())
            .forEach((key: string) => marginRequest[key.toLowerCase()] = this.globalConfig.formGroup.get('global').value / 100);

        this.onChangeQuotationMargin.emit({
            calculationMode: this.calculationMode,
            marginRequest,
        });

        this.closeModal.emit(true);
    }

    public submitCategories(): void {
        if(this.marginTotalFocuser){
            this.marginTotalFocuser.nativeElement.blur();
            this.navigationInsideModal = 0;
        }
        this.menu.closeMenu();

        const marginRequest: { [key: string]: number } = {};
        for (const value of this.initialCategoryValues.entries()) {
            marginRequest[value[0].toLowerCase()] = this.formGroup.get(value[0].toLowerCase()).value / 100;
        }

        this.onChangeQuotationMargin.emit({
            calculationMode: this.calculationMode,
            marginRequest,
        });

        this.closeModal.emit(false);
    }

    public changeNavigationInsideModal(index: number): void {
        this.navigationInsideModal = index;
    }

    public emitRightPosition(position: number): void {
        if (this.canEmitRightPosition) {
            this.onRightPositionChange.emit(position);
        }
    }

    // Resetting category values thanks to configuration
    public resetWithConfigValues(): void {
        this.usedCategoriesKeys.map((categoryKey: QuotationBlockCategoryCodeEnum): void => {
            const categoriesFound: ICalculationConfig[] = (this.retailCalculationConfig as ICalculationConfig).category.filter(
                (category: ICalculationCategoryValue): boolean => category.code === categoryKey.toLowerCase());

            const categoryPercentage: number = categoriesFound && categoriesFound.length ?
                ('commission' in categoriesFound[0] ?
                    categoriesFound[0].commission :
                    categoriesFound[0].margin) :
                ('commission' in this.retailCalculationConfig ?
                    (this.retailCalculationConfig as ICalculationConfig).commission :
                    (this.retailCalculationConfig as ICalculationConfig).margin);

            this.formGroup.get(categoryKey.toLowerCase()).setValue(categoryPercentage * 100);
        });
    }


    private radioValue(value: string): void {
        switch (value) {
            case ("global"):
                this.navigationInsideModal = 0;
                this.rowMax = this.rowMaxGlobal;
                break;
            case ("perCategory"):
                this.navigationInsideModal = 1;
                this.rowMax = Array.from(this.usedCategories.keys()).length + 2;
                break;
            default:
                break;
        }
    }

    private initializeModalPercents(): void {
        this.configurationResourceService
            .get(['wui', 'app_quotation', 'config', 'json-configuration', 'block-category.json'], defaultBlockCategoryConfig)
            .subscribe((blockCategory: IBlockCategoryConfig[]) => {
                //first loop to define global categories formGroup
                for (const categoryValue of this.usedCategories.entries()) {
                    const control: FormControl = this.formBuilder.control(categoryValue[1] ? (categoryValue[1] * 100).toFixed(2) : '0');
                    if (!this.formGroup) {
                        this.formGroup = new FormGroup({[categoryValue[0].toLowerCase()]: control});
                    } else {
                        this.formGroup.addControl(
                            categoryValue[0].toLowerCase(),
                            control
                        );
                    }
                }
                this.usedCategoriesKeys = [];
                for (const categoryValue of this.usedCategories.entries()) {
                    const categoryCode: QuotationBlockCategoryCodeEnum = categoryValue[0];
                    this.usedCategoriesKeys.push(categoryCode);
                    const blockCategoryParam: IBlockCategoryConfig = blockCategory
                        .find((blockCategoryItem: IBlockCategoryConfig) => blockCategoryItem.code.toLowerCase() === categoryCode.toLowerCase());
                    const blockCategoryParamMisc: IBlockCategoryConfig = blockCategory
                        .find((blockCategoryItem: IBlockCategoryConfig) => blockCategoryItem.code.toLowerCase() === 'miscellaneous');
                    const defaultIcon: string = blockCategoryParamMisc ? blockCategoryParamMisc.icon : 'fa fa-dollar';
                    const label: string = blockCategoryParam ? blockCategoryParam.wording : categoryCode;
                    const labelIcon: string = blockCategoryParam ? blockCategoryParam.icon : defaultIcon;

                    this.blockCategory.set(
                        categoryCode,
                        {
                            formGroup: this.formGroup,
                            formControlName: categoryCode.toLowerCase(),
                            label,
                            layout: FieldSpecsLayout.inline,
                            labelIcon,
                            unit: "%",
                        },
                    );
                }

                const globalDefault : number = this.retailCalculationConfig && this.retailCalculationConfig[this.calculationMode] ?
                    this.retailCalculationConfig[this.calculationMode]*100 : 0;
                if (!this.globalConfig) {
                    this.globalConfig = {
                        formGroup: new FormGroup({
                            global: this.formBuilder.control(null),
                        }),
                        formControlName: 'global',
                        label: this.wording.global,
                        layout: FieldSpecsLayout.inline,
                        unit: "%",
                    };
                }
                this.globalConfig.formGroup.get('global').patchValue(this.price ?
                    (this.price[this.calculationMode].used * 100).toFixed(2) :
                    globalDefault.toString());
            });
    }


    // Changing wording
    private changeWordingForCommission(): void {
        if(this.calculationMode === CalculationMode.commission){
            this.wording = {
                [WordingKeys.granularity]: this.wording[WordingKeys.granularity],
                [WordingKeys.global]:Yii.t('wui-quotation-js', 'Commission globale'),
                [WordingKeys.perCategory]: Yii.t('wui-quotation-js', 'Commission par catégorie de blocs'),
                [WordingKeys.valid]: this.wording[WordingKeys.valid],
                [WordingKeys.reset]: this.wording[WordingKeys.reset],
            }
        }
    }


}
